import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, Bell, BellRing, Bot, Building2, Check, Mail, PlugZap, Settings2, Sparkles } from 'lucide-react';
import type { AppNotification, NotificationKind } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime } from '../../lib/format';
import { useNotificationHistory, useNotifications } from '../../lib/notifications';
import { EmptyState, ErrorState, Skeleton } from '../ui';
import { Popover } from '../ui/Overlay';

const ICON: Record<NotificationKind, { icon: JSX.Element; className: string }> = {
  mail: { icon: <Mail className="h-4 w-4" />, className: 'bg-primary-soft text-primary-ink' },
  ai_draft: { icon: <Sparkles className="h-4 w-4" />, className: 'bg-[#EDE9FE] text-[#6D28D9] dark:bg-[#2E1F55] dark:text-[#C4B5FD]' },
  ai_analysis: { icon: <Bot className="h-4 w-4" />, className: 'bg-[#EDE9FE] text-[#6D28D9] dark:bg-[#2E1F55] dark:text-[#C4B5FD]' },
  auto_reply: { icon: <BellRing className="h-4 w-4" />, className: 'bg-success-soft text-success-ink' },
  reminder: { icon: <BellRing className="h-4 w-4" />, className: 'bg-warn-soft text-warn-ink' },
  send_failed: { icon: <AlertTriangle className="h-4 w-4" />, className: 'bg-danger-soft text-danger-ink' },
  account: { icon: <PlugZap className="h-4 w-4" />, className: 'bg-warn-soft text-warn-ink' },
  workspace: { icon: <Building2 className="h-4 w-4" />, className: 'bg-info-soft text-info-ink' },
};

function target(n: AppNotification): string {
  if (n.threadId) return `/inbox/${n.threadId}`;
  if (n.kind === 'account') return '/settings/accounts';
  if (n.kind === 'workspace') return '/settings/organization';
  return '/reminders';
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

/** Bell in the top bar with the unseen count; opens the notification centre as a popover. */
export function NotificationBell() {
  const { unseenCount } = useNotifications();
  return (
    <Popover
      label="Notifications"
      align="right"
      width={400}
      trigger={(p, open) => (
        <button
          type="button"
          {...p}
          aria-label={unseenCount ? `Notifications, ${unseenCount} new` : 'Notifications'}
          data-tip="Notifications"
          className={clsx('tip tip-bottom-end relative flex h-10 w-10 items-center justify-center rounded-full text-muted transition-colors hover:bg-raised hover:text-ink', open && 'bg-primary-soft text-primary-ink')}
        >
          <Bell className="h-[19px] w-[19px]" />
          {unseenCount > 0 && (
            <span className="absolute right-1 top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-danger px-1 text-[0.625rem] font-bold leading-none text-white ring-2 ring-[rgb(var(--surface))]">
              {unseenCount > 9 ? '9+' : unseenCount}
            </span>
          )}
        </button>
      )}
    >
      {(close) => <NotificationList onClose={close} />}
    </Popover>
  );
}

function NotificationList({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const history = useNotificationHistory(true);
  const { preview } = useNotifications();
  const [seenAt, setSeenAt] = useState<number>(() => (history.data?.seenAt ? Date.parse(history.data.seenAt) : 0));

  // Remember what was unseen when the panel opened, then mark everything as seen.
  useEffect(() => {
    const t = setTimeout(() => {
      void api('POST /api/notifications/seen')
        .then((r) => qc.setQueryData(qk.notifications, (prev: typeof history.data) => (prev ? { ...prev, seenAt: r.seenAt } : prev)))
        .catch(() => undefined);
    }, 800);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (history.data?.seenAt && seenAt === 0) setSeenAt(Date.parse(history.data.seenAt));
  }, [history.data?.seenAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = history.data?.notifications ?? [];
  const groups: { day: string; items: AppNotification[] }[] = [];
  for (const n of items) {
    const day = dayLabel(n.createdAt);
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.items.push(n);
    else groups.push({ day, items: [n] });
  }
  const unseen = items.filter((n) => Date.parse(n.createdAt) > seenAt).length;

  return (
    <div className="flex max-h-[min(72vh,640px)] flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-3.5">
        <div>
          <h2 className="text-[0.9375rem] font-semibold text-ink">Notifications</h2>
          <p className="text-xs text-muted">{unseen ? `${unseen} new` : 'All caught up'}</p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => void preview('mail')} data-tip="Preview the island" className="tip flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink" aria-label="Preview the notification island">
            <Sparkles className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate('/settings/notifications');
            }}
            data-tip="Notification settings"
            className="tip tip-bottom-end flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink"
            aria-label="Notification settings"
          >
            <Settings2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 scroll-thin">
        {history.isPending ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-2xl" />
            ))}
          </div>
        ) : history.error ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} compact />
        ) : items.length === 0 ? (
          <EmptyState icon={<Check className="h-6 w-6" />} title="You're all caught up" description="New email, AI drafts and automation updates appear here and in the island at the top of your screen." />
        ) : (
          groups.map((g) => (
            <section key={g.day} aria-label={g.day}>
              <h3 className="sticky top-0 z-10 bg-surface/95 px-3 pb-1 pt-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle backdrop-blur">{g.day}</h3>
              <ul className="space-y-0.5">
                {g.items.map((n) => {
                  const isNew = Date.parse(n.createdAt) > seenAt;
                  const ic = ICON[n.kind];
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          navigate(target(n));
                        }}
                        className="group flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-raised"
                      >
                        <span className={clsx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-105', ic.className)}>{ic.icon}</span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-2">
                            <span className={clsx('min-w-0 flex-1 truncate text-[0.8125rem] text-ink', isNew ? 'font-semibold' : 'font-medium')}>{n.title}</span>
                            <span className="shrink-0 text-[0.6875rem] text-subtle">{relativeTime(n.createdAt)}</span>
                          </span>
                          <span className="block truncate text-xs text-muted">{n.body}</span>
                          {n.accountLabel && (
                            <span className="mt-1 inline-flex items-center gap-1 text-[0.625rem] font-medium text-subtle">
                              <span className="h-1.5 w-1.5 rounded-full" style={{ background: n.accountColor ?? 'currentColor' }} aria-hidden />
                              {n.accountLabel}
                            </span>
                          )}
                        </span>
                        {isNew && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="New" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
